import { useMemo, useState } from 'react'
import {
  Check,
  ChevronsUpDown,
  CircleAlert,
  GitBranch,
  Lock,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { $api } from '@/api/client'
import { describeError } from '@/lib/api-error'
import type { components } from '@/api/openapi'
import { cn } from '@/lib/utils'

export type GitHubRepo = components['schemas']['GitHubRepo']

type RepoComboboxProps = {
  value: string
  onChange: (next: string) => void
  onSelectRepo?: (repo: GitHubRepo) => void
  placeholder?: string
  disabled?: boolean
  harnessOnly?: boolean
  className?: string
  triggerClassName?: string
  contentWidth?: string
  popoverContainer?: HTMLElement | null
  ['aria-invalid']?: boolean
}

function repoMatches(repo: GitHubRepo, q: string): boolean {
  if (!q) return true
  const lower = q.toLowerCase()
  return repo.full_name.toLowerCase().includes(lower)
}

function RepoGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      focusable={false}
      className={className}
      fill="currentColor"
    >
      <path d="M12 .5C5.65.5.5 5.66.5 12.04c0 5.1 3.29 9.41 7.86 10.94.58.1.79-.25.79-.56v-2.18c-3.2.7-3.87-1.36-3.87-1.36-.52-1.33-1.27-1.69-1.27-1.69-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.76 2.69 1.25 3.34.96.1-.74.4-1.25.72-1.54-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.18-3.1-.12-.29-.51-1.46.11-3.05 0 0 .96-.31 3.16 1.18.92-.26 1.9-.39 2.88-.39.98 0 1.96.13 2.88.39 2.2-1.49 3.16-1.18 3.16-1.18.62 1.59.23 2.76.11 3.05.74.81 1.18 1.84 1.18 3.1 0 4.42-2.69 5.4-5.25 5.69.41.36.78 1.06.78 2.13v3.16c0 .31.21.67.8.55C20.21 21.45 23.5 17.13 23.5 12.04 23.5 5.66 18.35.5 12 .5z" />
    </svg>
  )
}

export function RepoCombobox({
  value,
  onChange,
  onSelectRepo,
  placeholder = 'Select a repository…',
  disabled,
  className,
  triggerClassName,
  contentWidth = 'var(--radix-popover-trigger-width)',
  popoverContainer,
  harnessOnly = false,
  'aria-invalid': ariaInvalid,
}: RepoComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const reposQuery = $api.useQuery(
    'get',
    '/github/repos',
    harnessOnly
      ? { params: { query: { harness_only: 'true' as const } } }
      : { params: { query: {} } },
    {
      staleTime: 60_000,
      refetchOnMount: false,
      enabled: open,
    },
  )

  const items: GitHubRepo[] = reposQuery.data?.items ?? []
  const trimmedSearch = search.trim()
  const filtered = useMemo(() => {
    if (!trimmedSearch) return items
    return items.filter((r) => repoMatches(r, trimmedSearch))
  }, [items, trimmedSearch])

  const exactMatch = trimmedSearch
    ? items.some((r) => r.full_name === trimmedSearch)
    : false
  const showCustomRow = trimmedSearch.length > 0 && !exactMatch

  const submitCustom = () => {
    const v = trimmedSearch
    if (!v) return
    onChange(v)
    setOpen(false)
    setSearch('')
  }

  const renderTriggerLabel = () => {
    if (!value) {
      return (
        <span className="text-muted-foreground font-sans text-sm truncate">
          {placeholder}
        </span>
      )
    }
    return <span className="font-mono text-[12.5px] truncate">{value}</span>
  }

  return (
    <div className={cn('flex w-full', className)}>
      <Popover
        open={open && !disabled}
        onOpenChange={(next) => !disabled && setOpen(next)}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={ariaInvalid || undefined}
            disabled={disabled}
            className={cn(
              'h-9 w-full justify-between gap-2 px-3 font-normal',
              triggerClassName,
            )}
          >
            <div className="flex min-w-0 items-center gap-2">
              <RepoGlyph className="size-3.5 shrink-0 text-muted-foreground" />
              {renderTriggerLabel()}
            </div>
            <ChevronsUpDown className="ml-2 size-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="p-0"
          style={{ width: contentWidth, minWidth: '20rem' }}
          container={popoverContainer ?? null}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search repositories…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList className="max-h-[300px]">
              {reposQuery.isLoading ? (
                <div className="space-y-2 px-3 py-4 text-xs text-muted-foreground">
                  <div className="shimmer h-4 w-2/3 rounded-md bg-muted" />
                  <div className="shimmer h-4 w-1/2 rounded-md bg-muted" />
                  <div className="shimmer h-4 w-3/5 rounded-md bg-muted" />
                </div>
              ) : null}

              {reposQuery.isError ? (
                <div className="flex items-start gap-2 px-3 py-3 text-xs text-destructive">
                  <CircleAlert className="size-3.5 shrink-0 mt-0.5" />
                  <span>
                    {describeError(reposQuery.error).message}
                  </span>
                </div>
              ) : null}

              {!reposQuery.isLoading &&
              !reposQuery.isError &&
              filtered.length === 0 &&
              !showCustomRow ? (
                <CommandEmpty>No repos found.</CommandEmpty>
              ) : null}

              {filtered.length > 0 ? (
                <CommandGroup heading="Repositories">
                  {filtered.map((repo) => (
                    <CommandItem
                      key={repo.full_name}
                      value={repo.full_name}
                      onSelect={() => {
                        onChange(repo.full_name)
                        onSelectRepo?.(repo)
                        setOpen(false)
                        setSearch('')
                      }}
                      className={cn(
                        'gap-2 py-2',
                        repo.archived && 'opacity-60',
                      )}
                    >
                      <Check
                        className={cn(
                          'size-3.5 shrink-0',
                          value === repo.full_name ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <div className="flex min-w-0 flex-1 flex-col leading-tight">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className={cn(
                              'truncate font-mono text-[12.5px]',
                              repo.archived && 'text-muted-foreground line-through',
                            )}
                          >
                            {repo.full_name}
                          </span>
                          {repo.private ? (
                            <Lock
                              className="size-3 shrink-0 text-muted-foreground"
                              aria-label="Private repository"
                            />
                          ) : null}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                          <GitBranch className="size-3" aria-hidden />
                          <span className="font-mono">{repo.default_branch}</span>
                          {repo.archived ? (
                            <span className="rounded-sm bg-muted px-1 py-px text-[10px] uppercase tracking-wider">
                              archived
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}

              {showCustomRow ? (
                <CommandGroup heading="Use custom value">
                  <CommandItem
                    value={`__custom__:${trimmedSearch}`}
                    onSelect={submitCustom}
                    className="gap-2"
                  >
                    <Sparkles className="size-3.5 text-amber-500" />
                    <span className="text-sm">
                      Use{' '}
                      <span className="font-mono text-[12.5px] text-foreground">
                        {trimmedSearch}
                      </span>
                    </span>
                  </CommandItem>
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
